import { Edit, RemoveCircle } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { IconButton } from '~/components/IconButton';
import { AuthorizedLink } from '~/components/links';
import type { ColumnDef } from '~/components/list-table';
import { Checkbox, DragHandle } from '~/components/list-table';
import { formatRuntime } from '~/lib/runtime';
import { EndsAtHeader, StartsAtHeader, StatusCell, ThumbnailCell } from '../cells';
import type { PlaylistDesign__PlaylistItem as PlaylistItem } from '../index.generated';

const formatDate = (date: string | null | undefined): string =>
  date ? DateTime.fromSQL(date).toLocaleString(DateTime.DATE_MED) : '—';

const getName = (item: PlaylistItem): string => item.contentItem.name;

export const useColumns = (onRemove?: (id: number) => void): readonly ColumnDef<PlaylistItem>[] => {
  const { t } = useTranslation();

  return [
    {
      key: 'check',
      label: '',
      width: 48,
      align: 'center',
      renderHeader: (context) => (
        <Checkbox
          checked={context?.allSelected || false}
          indeterminate={!context?.allSelected && context?.someSelected}
          label={t('common.select_all')}
          onChange={context?.onSelectAll}
        />
      ),
      renderCell: (item, context) => (
        <Checkbox
          checked={context?.checked || false}
          label={t('common.actions.select_item', { name: getName(item) })}
          onChange={() => context?.onToggle?.(item.id)}
        />
      ),
    },
    {
      key: 'handle',
      label: '',
      width: 32,
      responsive: 'sm-up',
      align: 'center',
      renderCell: (_item, context) => (
        <DragHandle
          ref={context?.setActivatorNodeRef}
          {...(context?.dragDisabled ? {} : context?.activatorProps)}
          dragDisabled={context?.dragDisabled}
          isDragging={context?.isDragging}
        />
      ),
    },
    {
      key: 'thumb',
      label: '',
      width: 80,
      responsive: 'sm-up',
      align: 'center',
      renderCell: (item) => <ThumbnailCell item={item} />,
    },
    {
      key: 'name',
      label: t('common.labels.name'),
      width: 'auto',
      renderCell: (item) => {
        const entity = item.contentItem.source || item.contentItem;
        return (
          <div
            title={getName(item)}
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            <AuthorizedLink authorized={!!entity.canUpdate.value} entity={entity} />
          </div>
        );
      },
    },
    {
      key: 'status',
      label: t('common.labels.status'),
      width: 96,
      responsive: 'lg-up',
      renderCell: (item) => <StatusCell item={item} />,
    },
    {
      key: 'duration',
      label: t('common.labels.duration'),
      width: 80,
      responsive: 'md-up',
      renderCell: (item) => <Typography noWrap>{formatRuntime(item.duration, 3)}</Typography>,
    },
    {
      key: 'starts',
      label: t('components.playlist_reorder_list.columns.starts'),
      width: 120,
      responsive: 'md-up',
      renderHeader: () => <StartsAtHeader />,
      renderCell: (item) => <Typography noWrap>{formatDate(item.startsAt)}</Typography>,
    },
    {
      key: 'ends',
      label: t('components.playlist_reorder_list.columns.ends'),
      width: 120,
      responsive: 'md-up',
      renderHeader: () => <EndsAtHeader />,
      renderCell: (item) => <Typography noWrap>{formatDate(item.endsAt)}</Typography>,
    },
    {
      key: 'actions',
      label: '',
      width: 80,
      responsive: 'md-up',
      renderCell: (item) => {
        const entity = item.contentItem.source || item.contentItem;
        if (!entity.canUpdate.value) return null;
        return (
          <>
            <IconButton label={t('components.playlist_reorder_list.edit_tooltip')}>
              <Edit color="secondary" fontSize="small" />
            </IconButton>
            {onRemove && (
              <IconButton
                label={t('common.actions.remove')}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(item.id);
                }}
              >
                <RemoveCircle color="error" fontSize="small" />
              </IconButton>
            )}
          </>
        );
      },
    },
  ] as const;
};
